import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import {
  FaPlus,
  FaCheck,
  FaTrashAlt,
  FaTimes,
  FaPaperPlane,
  FaCalendarCheck,
  FaCalendarPlus,
  FaHistory,
} from "react-icons/fa";

import { useDailyActivityLogger } from "./UsedDailyActivityLogger";
import DailyLogSummaryCards from "./DailyLogSummaryCards";
import DailyLogTaskRow, { TaskCheckbox } from "./DailyLogTaskRow";
import OverdueBanner from "./OverdueBanner";
import TaskFormModal from "./TaskFormModal";
import TaskViewModal from "./TaskViewModal";
import TaskHistoryPanel from "./TaskHistoryPanel";
import { formatLogDate, todayIsoDate, filterTodayReportTasks } from "./DailyActivityLoggerUtils";
import { dailyActivityApi } from "./dailyActivityApi";
import { mapApiTaskToFrontend } from "./dailyActivityMappers";

import "../../Clients/clients.css";
import "./DailyActivityLogger.css";

const TABS = [
  {
    id: "today",
    label: "Today Tasks",
    subtitle: "Track and complete today's work",
    icon: FaCalendarCheck,
  },
  {
    id: "future",
    label: "Plan for Tomorrow",
    subtitle: "Prepare tasks for the next day",
    icon: FaCalendarPlus,
  },
  {
    id: "history",
    label: "Task History",
    subtitle: "View past reports and activity",
    icon: FaHistory,
  },
];

function DailyActivityLogger() {
  const {
    tasks,
    summary,
    selectedDate,
    loading,
    createTaskFromModal,
    updateTaskFromModal,
    requestDeleteTask,
    selectedIds,
    isAllSelected,
    toggleSelect,
    toggleSelectAll,
    clearSelection,
    bulkComplete,
    bulkDelete,
    reorderTasks,
    rescheduleAllOverdue,
    overdueCount,
    showOverdueBanner,
    dismissOverdueBanner,
    loadTasks,
    sendTodayReport,
    sendTomorrowPlan,
    sendingReport,
  } = useDailyActivityLogger();

  const [activeTab, setActiveTab] = useState("today");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [editingTask, setEditingTask] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewingTask, setViewingTask] = useState(null);
  const [saving, setSaving] = useState(false);

  const [futureTasks, setFutureTasks] = useState([]);
  const [futureLoading, setFutureLoading] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const fetchFutureTasks = useCallback(async () => {
    setFutureLoading(true);
    try {
      const res = await dailyActivityApi.getFutureTasks();
      const list = (res?.data?.data?.tasks || []).map(mapApiTaskToFrontend);
      setFutureTasks(list);
    } catch (err) {
      console.error(err);
      setFutureTasks([]);
    } finally {
      setFutureLoading(false);
    }
  }, []);

  const handleSendTodayReport = () => {
    if (typeof sendTodayReport !== "function") return;
    sendTodayReport(() => {
      loadTasks?.(selectedDate);
      fetchFutureTasks();
    });
  };

  const handleSendTomorrowPlan = () => {
    if (typeof sendTomorrowPlan !== "function") return;
    sendTomorrowPlan(futureTasks.length, () => {
      fetchFutureTasks();
    });
  };

  // Fetch the correct data when the active tab changes.
  // Today uses the dedicated today-tasks endpoint (via loadTasks).
  useEffect(() => {
    if (activeTab === "today") {
      loadTasks?.(selectedDate);
    } else if (activeTab === "future") {
      fetchFutureTasks();
    }
    clearSelection();
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDragEnd = ({ active, over }) => {
    if (over && active.id !== over.id) reorderTasks(active.id, over.id);
  };

  const modalContext = activeTab === "future" ? "future" : "today";

  const openCreateModal = () => {
    setModalMode("create");
    setEditingTask(null);
    setModalOpen(true);
  };

  const openEditModal = (task) => {
    if (task.is_finalized) return;
    setModalMode("edit");
    setEditingTask(task);
    setModalOpen(true);
  };

  const openViewModal = (task) => {
    setViewingTask(task);
    setViewModalOpen(true);
  };

  const handleModalSubmit = async (formData) => {
    setSaving(true);
    let ok = false;

    if (modalMode === "create") {
      const payload =
        modalContext === "future"
          ? { ...formData, scheduled_date: formData.scheduled_date }
          : { ...formData, scheduled_date: todayIsoDate() };
      ok = await createTaskFromModal(payload, modalContext);
    } else if (editingTask) {
      ok = await updateTaskFromModal(editingTask.id, formData, modalContext);
    }

    setSaving(false);
    if (ok) {
      setModalOpen(false);
      if (activeTab === "future" || modalContext === "future") {
        fetchFutureTasks();
      } else {
        loadTasks?.(selectedDate);
      }
    }
  };

  const taskCount =
    activeTab === "future" ? futureTasks.length : tasks.length;
  const isListTab = activeTab === "today" || activeTab === "future";
  const listTasks = activeTab === "future" ? futureTasks : tasks;
  const selectableIds = useMemo(
    () => listTasks.filter((task) => !task.is_finalized).map((task) => task.id),
    [listTasks]
  );
  const allVisibleSelected = isAllSelected(selectableIds);
  const hasSelection = selectedIds.size > 0;
  const todayReportCount = useMemo(
    () => filterTodayReportTasks(tasks, selectedDate).length,
    [tasks, selectedDate]
  );

  const handleBulkComplete = () => {
    bulkComplete(() => {
      if (activeTab === "future") fetchFutureTasks();
      else loadTasks?.(selectedDate);
    });
  };

  const handleBulkDelete = () => {
    bulkDelete(() => {
      if (activeTab === "future") fetchFutureTasks();
      else loadTasks?.(selectedDate);
    });
  };

  const selectionBar = hasSelection ? (
    <>
      {activeTab !== "future" ? (
        <button
          type="button"
          className="client-invoices-xero__tool-btn dal-bulk-tool-btn"
          onClick={handleBulkComplete}
        >
          <FaCheck className="me-1" /> Complete
        </button>
      ) : null}
      <button
        type="button"
        className="client-invoices-xero__tool-btn dal-bulk-tool-btn"
        onClick={handleBulkDelete}
      >
        <FaTrashAlt className="me-1" /> Delete
      </button>
      <button
        type="button"
        className="client-invoices-xero__tool-btn dal-bulk-tool-btn"
        onClick={clearSelection}
      >
        <FaTimes className="me-1" /> Cancel
      </button>
      <span className="client-invoices-xero__selection-label">
        {selectedIds.size} task{selectedIds.size === 1 ? "" : "s"} selected
      </span>
    </>
  ) : null;

  return (
    <div className="tailwind-container">
      <div className="client-invoices-xero dal-xero-shell">
        <div className="client-invoices-xero__page-head">
          <h1 className="client-invoices-xero__title">My Tasks</h1>
          <p className="client-invoices-xero__subtitle">
            Track your daily work and outcomes · {formatLogDate(selectedDate)}
          </p>
        </div>

        <DailyLogSummaryCards summary={summary} />

        <nav
          className="client-invoices-xero__module-switch dal-module-switch--three"
          role="tablist"
          aria-label="Task sections"
        >
          {TABS.map((tab) => {
            const isActive = activeTab === tab.id;
            const Icon = tab.icon;
            const count =
              tab.id === "today"
                ? tasks.length
                : tab.id === "future"
                ? futureTasks.length
                : null;

            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={`client-invoices-xero__module-switch-item${
                  isActive ? " is-active" : ""
                }`}
                onClick={() => setActiveTab(tab.id)}
              >
                <span className="client-invoices-xero__module-switch-icon">
                  <Icon aria-hidden="true" />
                </span>
                <span className="client-invoices-xero__module-switch-copy">
                  <span className="client-invoices-xero__module-switch-label">
                    {tab.label}
                    {count != null ? (
                      <span className="client-invoices-xero__module-switch-badge">
                        {count}
                      </span>
                    ) : null}
                  </span>
                  <span className="client-invoices-xero__module-switch-subtitle">
                    {tab.subtitle}
                  </span>
                </span>
              </button>
            );
          })}
        </nav>

        {isListTab && (
          <div className="client-invoices-xero__actions dal-page-actions">
            {activeTab === "today" && (
              <button
                type="button"
                className="client-invoices-xero__btn client-invoices-xero__btn--primary"
                onClick={handleSendTodayReport}
                disabled={loading || sendingReport || todayReportCount === 0}
              >
                <FaPaperPlane />
                {sendingReport ? "Sending..." : "Send Today Report"}
              </button>
            )}
            {activeTab === "future" && (
              <button
                type="button"
                className="client-invoices-xero__btn client-invoices-xero__btn--primary"
                onClick={handleSendTomorrowPlan}
                disabled={futureLoading || sendingReport || futureTasks.length === 0}
              >
                <FaPaperPlane />
                {sendingReport ? "Submitting..." : "Send Tomorrow Plan"}
              </button>
            )}
            <button
              type="button"
              className="client-invoices-xero__btn client-invoices-xero__btn--primary"
              onClick={openCreateModal}
              disabled={activeTab === "today" && loading}
            >
              <FaPlus />
              Add Task
            </button>
          </div>
        )}

        {activeTab === "today" && showOverdueBanner && (
          <OverdueBanner
            count={overdueCount}
            onRescheduleAll={rescheduleAllOverdue}
            onDismiss={dismissOverdueBanner}
          />
        )}

        {activeTab === "today" && (
          <TaskTable
            tasks={tasks}
            loading={loading}
            selectedDate={selectedDate}
            selectedIds={selectedIds}
            allVisibleSelected={allVisibleSelected}
            sensors={sensors}
            onDragEnd={handleDragEnd}
            onToggleSelect={toggleSelect}
            onToggleSelectAll={() => toggleSelectAll(selectableIds)}
            onView={openViewModal}
            onEdit={openEditModal}
            onDelete={requestDeleteTask}
            hideDueColumn
            selectionBar={selectionBar}
            summaryLabel={`${taskCount} task${taskCount === 1 ? "" : "s"}`}
            emptyTitle="No tasks for today"
            emptyHint="Add your first task to get started."
            onAddTask={openCreateModal}
          />
        )}

        {activeTab === "future" && (
          <TaskTable
            tasks={futureTasks}
            loading={futureLoading}
            selectedDate={selectedDate}
            selectedIds={selectedIds}
            allVisibleSelected={allVisibleSelected}
            sensors={sensors}
            onDragEnd={() => {}}
            onToggleSelect={toggleSelect}
            onToggleSelectAll={() => toggleSelectAll(selectableIds)}
            onView={openViewModal}
            onEdit={openEditModal}
            onDelete={requestDeleteTask}
            hideDueColumn={false}
            hideDrag
            selectionBar={selectionBar}
            summaryLabel={`${taskCount} task${taskCount === 1 ? "" : "s"}`}
            emptyTitle="Nothing planned for tomorrow"
            emptyHint="Add tasks to build your tomorrow plan."
            onAddTask={openCreateModal}
          />
        )}

        {activeTab === "history" && (
          <div className="client-invoices-xero__table-card">
            <TaskHistoryPanel isActive={activeTab === "history"} />
          </div>
        )}
      </div>

      <TaskFormModal
        open={modalOpen}
        mode={modalMode}
        initialTask={editingTask}
        selectedDate={selectedDate}
        context={modalContext}
        saving={saving}
        onClose={() => setModalOpen(false)}
        onSubmit={handleModalSubmit}
      />

      <TaskViewModal
        open={viewModalOpen}
        task={viewingTask}
        context={activeTab === "future" ? "future" : "today"}
        onClose={() => {
          setViewModalOpen(false);
          setViewingTask(null);
        }}
      />
    </div>
  );
}

function TaskTable({
  tasks,
  loading,
  selectedDate,
  selectedIds,
  allVisibleSelected,
  sensors,
  onDragEnd,
  onToggleSelect,
  onToggleSelectAll,
  onView,
  onEdit,
  onDelete,
  hideDueColumn = false,
  hideDrag = false,
  hideActions = false,
  selectionBar = null,
  summaryLabel = "",
  emptyTitle = "No tasks yet",
  emptyHint = "Add a task to start tracking your work.",
  onAddTask,
}) {
  return (
    <>
      <div
        className={`client-invoices-xero__toolbar${
          selectionBar ? "" : " dal-task-table-toolbar"
        }`}
      >
        {selectionBar ? (
          <div className="client-invoices-xero__toolbar-left">{selectionBar}</div>
        ) : null}
        <div className="client-invoices-xero__toolbar-right">
          <span className="client-invoices-xero__summary">{summaryLabel}</span>
        </div>
      </div>

      <div className="client-invoices-xero__table-card">
        {loading ? (
          <div className="client-invoices-xero__loading">Loading tasks...</div>
        ) : tasks.length === 0 ? (
          <div className="client-invoices-xero__empty">
            <p className="mb-1 fw-semibold">{emptyTitle}</p>
            <p className="mb-3 text-muted">{emptyHint}</p>
            {typeof onAddTask === "function" ? (
              <button
                type="button"
                className="client-invoices-xero__btn client-invoices-xero__btn--primary"
                onClick={onAddTask}
              >
                <FaPlus /> Add Task
              </button>
            ) : null}
          </div>
        ) : (
          <div className="dal-task-table-wrap cms-commissions-table-wrap table-responsive">
            <table className="client-invoices-xero__table dal-task-table">
              <thead>
                <tr>
                  {!hideDrag && <th className="dal-drag-col" aria-label="Reorder" />}
                  <th className="client-invoices-xero__check-col">
                    <TaskCheckbox
                      checked={allVisibleSelected}
                      disabled={tasks.length === 0}
                      onClick={onToggleSelectAll}
                      title="Select all tasks"
                    />
                  </th>
                  <th>Task</th>
                  <th>Priority</th>
                  <th>Status</th>
                  {!hideDueColumn && <th>Due</th>}
                  <th>Notes</th>
                  {!hideActions && <th className="text-end">Actions</th>}
                </tr>
              </thead>

              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={onDragEnd}
              >
                <SortableContext
                  items={tasks.map((t) => t.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <tbody>
                    {tasks.map((task) => (
                      <DailyLogTaskRow
                        key={task.id}
                        task={task}
                        isFinalized={Boolean(task.is_finalized)}
                        selectedDate={selectedDate}
                        isSelected={selectedIds.has(task.id)}
                        onToggleSelect={onToggleSelect}
                        onView={onView}
                        onEdit={onEdit}
                        onDelete={onDelete}
                        hideDue={hideDueColumn}
                        hideDrag={hideDrag}
                        hideActions={hideActions}
                      />
                    ))}
                  </tbody>
                </SortableContext>
              </DndContext>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

export default DailyActivityLogger;